import React, { useState, useEffect } from 'react';
import { savedCompaniesApi } from '../../../services/prospector/savedCompanies';
import type { SavedCompanyList } from '../../../types/prospector/savedCompanies.types';

// Define Company interface locally
interface Company {
  organisasjonsnummer: string;
  navn: string;
  naeringskode1?: {
    kode: string;
    beskrivelse: string;
  };
  antallAnsatte?: number;
}

interface SaveCompanyProps {
  company: Company;
  onSuccess?: () => void;
  isDropdown?: boolean;
}

export default function SaveCompany({ company, onSuccess, isDropdown = false }: SaveCompanyProps) {
  const [lists, setLists] = useState<SavedCompanyList[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Fetch user's lists on component mount
  useEffect(() => {
    const fetchLists = async () => {
      setLoading(true);
      try {
        const userLists = await savedCompaniesApi.getLists();
        setLists(userLists);
        // Select the first list by default if any exist
        if (userLists.length > 0) {
          setSelectedListId(userLists[0].id || null);
        }
      } catch (error) {
        console.error('Error fetching company lists:', error);
        setStatusMessage('Kunne ikke hente bedriftslister');
      } finally {
        setLoading(false);
      }
    };

    fetchLists();
  }, []);

  // Handle saving company to selected list
  const handleSaveToList = async () => {
    if (!selectedListId) {
      setStatusMessage('Vennligst velg en liste');
      return;
    }

    setSaveStatus('saving');
    setStatusMessage('Lagrer bedrift...');

    try {
      // Make sure antallAnsatte is a number if it exists
      const employeeCount =
        company.antallAnsatte !== undefined ?
        Number(company.antallAnsatte) : undefined;

      // Create the data object we'll send
      const companyData = {
        organizationNumber: company.organisasjonsnummer,
        companyName: company.navn,
        industryCode: company.naeringskode1?.kode,
        industryDescription: company.naeringskode1?.beskrivelse,
        antallAnsatte: employeeCount,
        dateAdded: new Date(),
      };

      // Log the complete data being sent
      console.log('Full company data being sent to API:', JSON.stringify(companyData, null, 2));

      // Send the request
      await savedCompaniesApi.addCompanyToList({
        listId: selectedListId,
        company: companyData
      });

      setSaveStatus('success');
      setStatusMessage('Company saved to list');

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset status after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
        setStatusMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving company to list:', error);
      setSaveStatus('error');
      setStatusMessage('Could not save company to list');
    }
  };

  // Handle creating a new list
  const handleCreateList = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newListName.trim()) {
      setStatusMessage('Vennligst angi et navn for listen');
      return;
    }

    setSaveStatus('saving');
    setStatusMessage('Oppretter ny liste...');

    try {
      const newList = await savedCompaniesApi.createList({
        name: newListName,
        description: newListDescription
      });

      setLists([...lists, newList]);
      setSelectedListId(newList.id || null);
      setNewListName('');
      setNewListDescription('');
      setShowNewListForm(false);

      setSaveStatus('success');
      setStatusMessage('Ny liste opprettet');

      // Reset status after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
        setStatusMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error creating new list:', error);
      setSaveStatus('error');
      setStatusMessage('Kunne ikke opprette ny liste');
    }
  };

  return (
    <div>
      {/* Status message */}
      {statusMessage && (
        <div className={`p-3 mb-4 rounded text-sm ${
          saveStatus === 'error' ? 'bg-red-100 text-red-700' :
          saveStatus === 'success' ? 'bg-green-100 text-green-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {statusMessage}
        </div>
      )}

      {/* List selection */}
      {!showNewListForm ? (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select list
            </label>
            <div className={`${isDropdown ? 'space-y-2' : 'flex'}`}>
              <select
                value={selectedListId || ''}
                onChange={(e) => setSelectedListId(Number(e.target.value))}
                className={`${isDropdown ? 'w-full mb-2' : 'flex-grow'} block p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white`}
                disabled={loading || lists.length === 0}
              >
                {lists.length === 0 && <option value="">No lists available</option>}
                {lists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.company_count || 0} companies)
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewListForm(true)}
                className={`${isDropdown ? 'w-full' : 'ml-2'} inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none`}
              >
                + Create New List
              </button>
            </div>
          </div>

          <button
            onClick={handleSaveToList}
            disabled={loading || saveStatus === 'saving' || !selectedListId}
            className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
              loading || saveStatus === 'saving' || !selectedListId
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 focus:outline-none'
            }`}
          >
            {saveStatus === 'saving' ? 'Saving...' : 'Save to List'}
          </button>
        </>
      ) : (
        // New list form
        <form onSubmit={handleCreateList}>
          <div className="mb-4">
            <label htmlFor="newListName" className="block text-sm font-medium text-gray-700 mb-1">
              Navn på ny liste
            </label>
            <input
              type="text"
              id="newListName"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
              placeholder="F.eks. Potensielle kunder"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="newListDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Beskrivelse (valgfritt)
            </label>
            <textarea
              id="newListDescription"
              value={newListDescription}
              onChange={(e) => setNewListDescription(e.target.value)}
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
              placeholder="Beskrivelse av listen"
              rows={3}
            />
          </div>
          
          <div className={`${isDropdown ? 'flex flex-col space-y-2' : 'flex justify-between'}`}>
            <button
              type="button"
              onClick={() => setShowNewListForm(false)}
              className={`${isDropdown ? 'w-full' : ''} inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading || saveStatus === 'saving' || !newListName.trim()}
              className={`${isDropdown ? 'w-full' : ''} inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                loading || saveStatus === 'saving' || !newListName.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none'
              }`}
            >
              {saveStatus === 'saving' ? 'Creating...' : 'Create List'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}